// Pure recommendation logic — the port of v3's `Landing/creativeAiRecommendations.ts` and
// `NewAnalysis/recommendationPrefill.ts`. No Vue, no HTTP, no store: the composable and the wizard
// both call in here, and every rule below is unit-tested against the v3 behaviour it replaces.
import type { AnalysisFilterPredicate } from '../models/analysis.types'
import type {
  AnalysisPrefill,
  RecommendationCard,
  RecommendationExecution,
  RecommendationFrame,
  RecommendationQuery,
  RecommendationSection,
} from '../models/recommendation.types'

import { stripHtml } from '@/shared/lib/string/stripHtml'

/** Rank ascending; a frame with no rank sinks to the bottom while keeping its relative order. */
function sortByRank(frames: readonly RecommendationFrame[]): RecommendationFrame[] {
  return [...frames].sort((left, right) => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
    return leftRank - rightRank
  })
}

/** The frame's own range wins; the execution's is the fallback (v3 `resolveRecommendationDate`). */
function resolveDate(frameValue: string | null, executionValue: string | null): string | null {
  return frameValue || executionValue || null
}

/**
 * Seed values for the New Analysis wizard, or `null` when the frame cannot describe an analysis.
 * Both a creative type and a resolvable date range are required — the wizard's own validity rule.
 */
export function frameToPrefill(
  frame: RecommendationFrame | null | undefined,
  execution?: RecommendationExecution | null,
): AnalysisPrefill | null {
  const dateFrom = resolveDate(frame?.date_from ?? null, execution?.date_from ?? null)
  const dateTo = resolveDate(frame?.date_to ?? null, execution?.date_to ?? null)
  if (!frame || !frame.creative_type || !dateFrom || !dateTo) return null

  return {
    name: frame.title ?? '',
    creative_type: frame.creative_type,
    analysis_type: 'overall',
    date_from: dateFrom,
    date_to: dateTo,
    filters: frame.filters.map((filter) => ({
      field: filter.field,
      value: filter.value,
      match_mode: filter.match_mode,
    })),
  }
}

/**
 * Map one execution to the rendered section, or `null` when there is nothing to show.
 *
 * v3 ended this pipeline with `.filter(r => r.prefillAnalysis || r.title || r.rationaleText)`.
 * That filter was a **no-op**: the line above it defaulted every missing title to a literal
 * `'Recommended analysis'`, so the predicate's second term was always truthy. v4 keeps the title
 * nullable (the fallback is an i18n key resolved in the component), which would have turned that
 * dead filter into a live one and silently dropped frames v3 rendered — so it is not carried over.
 */
export function mapExecutionToSection(
  execution: RecommendationExecution | null | undefined,
): RecommendationSection | null {
  if (!execution?.recommendations.length) return null

  const recommendations: RecommendationCard[] = sortByRank(execution.recommendations).map(
    (frame) => ({
      frameId: frame.frame_id,
      title: frame.title,
      // Presentation-only default: the icon and the asset-count wording need a kind even when the
      // frame carries none. `prefill` stays null in that case, so such a card cannot be run.
      creativeType: frame.creative_type ?? 'image',
      rationaleText: stripHtml(frame.rationale_html ?? ''),
      estimatedAssetCount: frame.estimated_asset_count,
      estimatedSpend: frame.estimated_spend,
      shareOfSpend: frame.share_of_spend,
      filterCount: frame.filters.length,
      filtersHuman: frame.filters_human,
      rank: frame.rank,
      prefill: frameToPrefill(frame, execution),
      frame,
    }),
  )

  return {
    dateFrom: execution.date_from,
    dateTo: execution.date_to,
    recommendations,
  }
}

/** Locate one frame inside an execution by its id (the `?frame=` deep-link half). */
export function findRecommendationFrame(
  execution: RecommendationExecution | null | undefined,
  frameId: string | null | undefined,
): RecommendationFrame | null {
  if (!execution?.recommendations.length || !frameId) return null
  return execution.recommendations.find((frame) => frame.frame_id === String(frameId)) ?? null
}

/** Order-insensitive value identity, so `['a','b']` and `['b','a']` count as untouched. */
function normalizeFilterValue(value: unknown): string {
  if (Array.isArray(value)) return JSON.stringify([...value].map(String).sort())
  return JSON.stringify(value)
}

/**
 * Restore the recommendation's ORIGINAL predicate for every field the user left alone.
 *
 * The wizard rebuilds its filters from form state, which flattens every match mode to the wizard's
 * own (`in`, `greater_than_or_equal_to`). A recommendation may have authored something else —
 * `contains`, `not_in` — and re-running it with `in` is a different analysis. So: same field AND
 * same value ⇒ take the source predicate back, match mode included; anything the user changed keeps
 * the wizard's shape. With no source filters this is the identity function.
 */
export function mergePrefillFilters(
  sourceFilters: readonly AnalysisFilterPredicate[] | null | undefined,
  editableFilters: readonly AnalysisFilterPredicate[],
): AnalysisFilterPredicate[] {
  if (!sourceFilters?.length) return [...editableFilters]

  return editableFilters.map((filter) => {
    const source = sourceFilters.find((item) => item.field === filter.field)
    if (!source) return filter
    if (normalizeFilterValue(source.value) !== normalizeFilterValue(filter.value)) return filter
    return { ...source }
  })
}

/**
 * Which branch a card click takes (v3 `handleRecommendationSelect`). Order matters: a workspace
 * with no label schema is sent to settings even when the frame itself is perfectly runnable,
 * because an analysis without labels produces nothing.
 */
export function resolveRecommendationAction(input: {
  hasLabels: boolean
  prefill: AnalysisPrefill | null
}): 'configure_labels' | 'run_analysis' | 'unavailable' {
  if (!input.hasLabels) return 'configure_labels'
  return input.prefill ? 'run_analysis' : 'unavailable'
}

/** First usable string of a route-query value (vue-router hands `string | string[] | null`). */
function singleQueryValue(value: unknown): string | null {
  if (Array.isArray(value)) return singleQueryValue(value[0])
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** Read the `?reco_execution=<id>&frame=<id>` pair. Both halves are required to act. */
export function readRecommendationQuery(query: Record<string, unknown>): RecommendationQuery {
  const executionId = singleQueryValue(query.reco_execution)
  const frameId = singleQueryValue(query.frame)
  return { executionId, frameId, shouldFetch: Boolean(executionId && frameId) }
}
